import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Icon from "@/components/base/Icon";
import SearchBar from "@/components/feature/SearchBar";
import { useFavorites } from "@/hooks/useFavorites";
import { getComicsByIds } from "@/utils/comic";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "分类", to: "/category/all" },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [favOpen, setFavOpen] = useState(false);
  const favRef = useRef<HTMLDivElement>(null);
  const { favorites } = useFavorites();
  const location = useLocation();
  const favComics = getComicsByIds(favorites);

  useEffect(() => {
    setMenuOpen(false);
    setFavOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (favRef.current && !favRef.current.contains(event.target as Node)) {
        setFavOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
      isActive
        ? "text-primary-600 bg-primary-50"
        : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
              <Icon name="ri-book-open-fill" className="w-5 h-5" />
            </span>
            <span className="font-heading text-lg font-bold text-foreground-950 whitespace-nowrap">
              羞羞色漫
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-2">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === "/"} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block flex-1 max-w-xs ml-auto">
            <SearchBar compact />
          </div>

          <div className="relative ml-auto lg:ml-0" ref={favRef}>
            <button
              type="button"
              onClick={() => setFavOpen((open) => !open)}
              aria-label="我的收藏"
              className="relative flex items-center justify-center w-10 h-10 rounded-md border border-background-300 bg-background-50 text-foreground-700 cursor-pointer transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <Icon name="ri-heart-3-line" className="w-5 h-5" />
              {favorites.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary-500 text-background-50 text-[10px] font-semibold flex items-center justify-center">
                  {favorites.length}
                </span>
              )}
            </button>

            {favOpen && (
              <div className="absolute right-0 mt-2 w-72 rounded-lg border border-background-200 bg-background-50 p-3 shadow-none">
                <p className="font-heading text-sm font-semibold text-foreground-950 mb-2">
                  我的收藏
                </p>
                {favComics.length === 0 ? (
                  <p className="text-xs text-foreground-500 py-4 text-center">
                    还没有收藏漫画，去发现喜欢的作品吧
                  </p>
                ) : (
                  <ul className="max-h-72 overflow-y-auto flex flex-col gap-1">
                    {favComics.map((comic) => (
                      <li key={comic.id}>
                        <Link
                          to={`/comic/${comic.id}`}
                          className="flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors hover:bg-background-100"
                        >
                          <span className="w-9 h-12 rounded overflow-hidden bg-background-200 shrink-0">
                            <img
                              src={comic.cover}
                              alt={comic.title}
                              className="w-full h-full object-cover object-top"
                            />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-medium text-foreground-900 line-clamp-1">
                              {comic.title}
                            </span>
                            <span className="block text-xs text-foreground-500 line-clamp-1">
                              {comic.author}
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 pt-2 border-t border-background-200 text-[11px] text-foreground-400">
                  收藏保存在本机浏览器（演示）
                </p>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="打开菜单"
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-md border border-background-300 bg-background-50 text-foreground-700 cursor-pointer"
          >
            <Icon name={menuOpen ? "ri-close-line" : "ri-menu-line"} className="w-5 h-5" />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <SearchBar className="mb-3" />
          <nav className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors ${
                    isActive
                      ? "bg-primary-50 text-primary-600"
                      : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}